// src/lib/errorCodes.ts
//
// What the web app says when something is refused (requirements: Error
// taxonomy; takenote_decisions.md 14.2, Phase 12). Two kinds of refusal reach
// these pages, and they are kept apart on purpose:
//
//   THE EDGE FUNCTION'S CODES   ERROR_CODES - the one fixed list. A copy of
//                               supabase/functions/_shared/errorCodes.ts and of
//                               the extension's, and `pnpm -C apps/extension
//                               test:errors` fails if the three disagree.
//   SUPABASE AUTH'S OWN CODES   GoTrue answers login, signup, resend and the
//                               password change with codes of its own. They are
//                               translated at the page into AUTH_ERROR_CODES, a
//                               short list of this app's, and never shown raw.
//
// The auth list is NOT folded into the API list: the Edge Function never
// returns those codes, and a shared list the server half of which could never
// be sent would make "the lists agree" mean less.
//
// NOTHING HERE MAY SAY WHETHER AN EMAIL OR USERNAME EXISTS (10.2, 7.2). Measured
// on the running GoTrue before this was written:
//
//   login, unknown address                  400 invalid_credentials
//   login, known address, wrong password    400 invalid_credentials
//   login, unconfirmed, RIGHT password      400 email_not_confirmed
//   resend, unknown / confirmed / pending   200 {}
//   resend, pending address, second time    429 over_email_send_rate_limit
//
// So "email not confirmed" is safe to say - it needs the password - and a
// rate-limited resend is NOT: only a registered, unconfirmed address is ever
// throttled, so VerifyEmailPage shows the same "sent" for it.

export const ERROR_CODES = [
  "UNAUTHENTICATED",
  "METHOD_NOT_ALLOWED",
  "UNKNOWN_ENDPOINT",
  "INVALID_REQUEST",
  "INTERNAL_ERROR",
  "PROFILE_REQUIRED",
  "USERNAME_TAKEN",
  "USERNAME_ALREADY_SET",
  "INVALID_USERNAME",
  "RECIPIENT_UNAVAILABLE",
  "LOOKUP_LIMIT_REACHED",
  "SEND_LIMIT_REACHED",
  "NOTE_TOO_LARGE",
  "REAUTH_REQUIRED",
  "ACCOUNT_ALREADY_ACTIVE_ON_ANOTHER_DEVICE",
  "KEY_NOT_ACTIVE",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const UNKNOWN_ERROR_MESSAGE = "Something went wrong. Try again.";

const MESSAGES: Record<ErrorCode, string> = {
  UNAUTHENTICATED: "Your sign-in has ended. Log in again.",
  METHOD_NOT_ALLOWED: UNKNOWN_ERROR_MESSAGE,
  UNKNOWN_ENDPOINT: "This page doesn't match the server. Reload it and try again.",
  INVALID_REQUEST: "This page doesn't match the server. Reload it and try again.",
  INTERNAL_ERROR: "Something went wrong on the server. Try again in a moment.",
  PROFILE_REQUIRED: "Finish setting up your account first.",
  USERNAME_TAKEN: "That username is taken. Try another.",
  USERNAME_ALREADY_SET: "This account already has a username.",
  INVALID_USERNAME: "Usernames are 3 to 20 letters and digits; a single dot, dash or underscore may sit inside.",
  RECIPIENT_UNAVAILABLE: "This username can't receive notes.",
  LOOKUP_LIMIT_REACHED: "Too many usernames that couldn't be found today. Try again after midnight UTC.",
  SEND_LIMIT_REACHED: "You've reached today's sending limit. It resets at midnight UTC.",
  NOTE_TOO_LARGE: "This note is too large to send.",
  REAUTH_REQUIRED: "Log in again to confirm it's you.",
  ACCOUNT_ALREADY_ACTIVE_ON_ANOTHER_DEVICE: "TakeNote is active on another browser for this account.",
  KEY_NOT_ACTIVE: "TakeNote is active on another browser.",
};

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && (ERROR_CODES as readonly string[]).includes(value);
}

export function messageForCode(code: unknown): string {
  return isErrorCode(code) ? MESSAGES[code] : UNKNOWN_ERROR_MESSAGE;
}

// ---- Supabase Auth ---------------------------------------------------------

export const AUTH_ERROR_CODES = [
  "INVALID_CREDENTIALS",
  "EMAIL_NOT_CONFIRMED",
  "CAPTCHA_FAILED",
  "RATE_LIMITED",
  "WEAK_PASSWORD",
  "PASSWORD_TOO_GUESSABLE",
  "PASSWORD_BREACHED",
  "SAME_PASSWORD",
  "NETWORK",
  "AUTH_FAILED",
] as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

const AUTH_MESSAGES: Record<AuthErrorCode, string> = {
  INVALID_CREDENTIALS: "That email and password didn't match.",
  EMAIL_NOT_CONFIRMED: "Confirm your email address first - use the link we sent you.",
  CAPTCHA_FAILED: "The human check didn't go through. Complete it again, then try again.",
  RATE_LIMITED: "Too many attempts. Wait a minute, then try again.",
  WEAK_PASSWORD: "Use at least 12 characters, with upper and lower case letters, a number and a symbol.",
  // Not from GoTrue: this app's own refusal, from the strength estimator on
  // Sign up and Reset password (passwordStrength.ts). About the password just
  // typed, so safe to say specifically - it says nothing about any address.
  PASSWORD_TOO_GUESSABLE: "That password is too easy to guess. Add another word or two.",
  // GoTrue's leaked-password check (HaveIBeenPwned, a hosted-project setting -
  // Phase 14). About the password just typed, so safe to say for the same
  // reason as the line above.
  PASSWORD_BREACHED: "That password has appeared in a data breach, so it isn't safe to use. Choose a different one.",
  SAME_PASSWORD: "That's your current password. Choose a different one.",
  NETWORK: "Could not reach the server. Check your connection and try again.",
  AUTH_FAILED: "That didn't work. Try again.",
};

/**
 * This app's code for a Supabase Auth error. Unknown codes are AUTH_FAILED,
 * never passed through: GoTrue's `msg` is its wording, not this product's, and
 * some of its codes say more than a page should (`user_already_exists`).
 */
export function authCodeFor(
  error: { code?: unknown; status?: unknown; reasons?: unknown } | null | undefined
): AuthErrorCode {
  const code = typeof error?.code === "string" ? error.code : "";
  // `weak_password` carries GoTrue's reasons (supabase-js AuthWeakPasswordError):
  // "length" and "characters" are the rule, "pwned" is the leaked-password check.
  // A leaked password is named even when a rule is also missed - no version of
  // it is usable, and the pages check the rule before any request anyway.
  if (code === "weak_password" && Array.isArray(error?.reasons) && error.reasons.includes("pwned")) {
    return "PASSWORD_BREACHED";
  }
  switch (code) {
    case "invalid_credentials":
      return "INVALID_CREDENTIALS";
    case "email_not_confirmed":
      return "EMAIL_NOT_CONFIRMED";
    case "captcha_failed":
      return "CAPTCHA_FAILED";
    case "over_request_rate_limit":
    case "over_email_send_rate_limit":
      return "RATE_LIMITED";
    case "weak_password":
      return "WEAK_PASSWORD";
    case "same_password":
      return "SAME_PASSWORD";
  }
  // No HTTP status at all means the request never got an answer.
  if (error && (error.status === 0 || error.status === undefined) && !code) return "NETWORK";
  return "AUTH_FAILED";
}

export function authMessageFor(code: AuthErrorCode): string {
  return AUTH_MESSAGES[code];
}
